import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/Button';
import { useDispatch, useSelector } from 'react-redux';
import { updateRegistrationData } from '../store/userSlice';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import { toast } from 'react-toastify';

const ABOUT_MAX_LENGTH = 200;

function Step3({ setStep }) {
  const axiosPrivate = useAxiosPrivate();
  const dispatch = useDispatch();
  const registrationData = useSelector((state) => state.user.registrationData);
  const userData = useSelector((state) => state.user.userData);

  // Текст "О себе" — хранится в Redux, чтобы не терялся при возврате на шаг
  const about = registrationData.about ?? '';
  const [isSaving, setIsSaving] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const textareaRef = useRef(null);

  // При первом входе подтягиваем ранее сохранённый в профиле текст
  useEffect(() => {
    if (!registrationData.about && userData?.about) {
      dispatch(updateRegistrationData({ about: userData.about }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    dispatch(updateRegistrationData({ about: e.target.value.slice(0, ABOUT_MAX_LENGTH) }));
  };

  const handleNext = async () => {
    const text = about.trim();

    // Пустой текст — просто идём дальше, ничего не сохраняем
    if (!text) {
      setStep(4);
      return;
    }

    setIsSaving(true);
    try {
      await axiosPrivate.put('/users/about', { about: text });
      setStep(4);
    } catch (error) {
      console.error('Ошибка сохранения текста "О себе":', error);
      toast.error('Не удалось сохранить текст');
      // Не блокируем регистрацию из-за ошибки сохранения
      setStep(4);
    } finally {
      setIsSaving(false);
    }
  };

  const hasText = about.trim().length > 0;

  return (
    <div className="flex flex-col items-center w-full overflow-y-auto overflow-x-hidden px-4 pb-[110px]">
      <h1 className="font-raleway font-semibold mt-6 text-white text-[20px]">Напишите пару слов о себе</h1>
      <p className="font-raleway font-light mt-2 text-white text-[16px]">Этот текст отобразится в вашей анкете</p>

      <div className="w-full mt-4 relative">
        <textarea
          ref={textareaRef}
          name="about"
          id="about"
          aria-label="О себе"
          value={about}
          onChange={handleChange}
          onFocus={() => setIsKeyboardOpen(true)}
          onBlur={() => setIsKeyboardOpen(false)}
          className="w-full h-[300px] resize-none overflow-y-auto bg-[#022424] rounded-lg p-4 pb-8 border border-[#233636] text-white placeholder:text-[#6B8A8A] focus:outline-none focus:ring-2 focus:ring-[#A1F69E] font-raleway text-[16px] leading-relaxed"
          placeholder="О себе"
          maxLength={ABOUT_MAX_LENGTH}
        />
        {/* Счётчик живёт в панели у клавиатуры, пока идёт ввод */}
        {!isKeyboardOpen && (
          <span className="absolute bottom-[12px] right-[16px] text-[12px] font-raleway text-[#6B8A8A] pointer-events-none bg-[#022424] px-1 rounded">
            {about.length}/{ABOUT_MAX_LENGTH}
          </span>
        )}
      </div>

      {/* Основные кнопки уезжают под клавиатуру, чтобы не закрывать текст */}
      <motion.div
        initial={false}
        animate={{ y: isKeyboardOpen ? '150%' : '0%', opacity: isKeyboardOpen ? 0 : 1 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        aria-hidden={isKeyboardOpen}
        style={{ pointerEvents: isKeyboardOpen ? 'none' : 'auto' }}
        className="fixed inset-x-0 bottom-[24px] px-4"
      >
        <Button className='w-full' onClick={handleNext} disabled={isSaving || !hasText}>
          {isSaving ? 'Сохранение...' : 'Далее'}
        </Button>
        <button
          type="button"
          className="font-raleway font-medium text-[18px] h-[64px] text-center w-full text-white opacity-50 hover:opacity-70 transition-opacity z-10"
          onClick={() => setStep(4)}
        >
          Пропустить
        </button>
      </motion.div>

      {/* Компактная панель у верхней кромки клавиатуры */}
      <motion.div
        initial={false}
        animate={{ y: isKeyboardOpen ? '0%' : '110%' }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        aria-hidden={!isKeyboardOpen}
        style={{ pointerEvents: isKeyboardOpen ? 'auto' : 'none' }}
        className="fixed inset-x-0 bottom-0 z-20 flex items-center justify-between px-4 py-2 bg-[#021A1A]/95 backdrop-blur-sm border-t border-[#233636]"
      >
        <span className="text-[12px] font-raleway text-[#6B8A8A]">
          {about.length}/{ABOUT_MAX_LENGTH}
        </span>
        <button
          type="button"
          onClick={() => textareaRef.current?.blur()}
          className="h-[36px] px-5 rounded-[400px] bg-[#a1f69e] text-[#022424] font-raleway font-semibold text-[15px]"
        >
          Готово
        </button>
      </motion.div>
    </div>
  );
}

export default Step3;
